import { Component, ViewChildren, ElementRef, inject, Input, OnInit, OnDestroy, QueryList } from '@angular/core';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { filter, takeUntil } from 'rxjs/operators';

import { ConfigurationFacade } from '../../facades/configuration.facade';
import { State } from '../../reducers';
import { IconUtils } from '../../utils/icon.utils';
import { IIndexedNotification } from '../../models/cpq.models';
import { PlatformLocation } from '@angular/common';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss'],
  standalone: false,
})
export class NotificationComponent implements OnInit, OnDestroy {
  active: boolean = false;
  @Input() disabled: boolean = false;
  @ViewChildren('notificationModal') notificationModals!: QueryList<ElementRef>;
  notifications: IIndexedNotification[] = [];

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  private clickSubscription!: Subscription;
  private timeoutId: number | null = null;
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly store: Store<State> = inject(Store<State>);

  constructor() {
    const location: PlatformLocation = inject(PlatformLocation);
    location.onPopState(() => {
      if (this.isNotification()) {
        this.clearAction();
      }
    });
  }

  ngOnInit(): void {
    this.configurationFacade.getNotification()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(notifications => {
        this.notifications = notifications;
        if (this.isNotification()) {
          this.active = true;
          setTimeout(() => {
            this.timeoutId = window.setTimeout(() => {
              if (this.notificationModals) {
                this.subscribeClick();
              }
            });
          });
        }
      });
  }


  toggleNotification(): void {
    if (this.disabled) {
      return;
    }
    if (!this.active) {
      this.active = true;
      setTimeout(() => {
        this.timeoutId = window.setTimeout(() => {
          if (this.notificationModals) {
            this.subscribeClick();
          }
        });
      });
    } else {
      this.active = false;
      this.unsubscribeClick();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
    }
  }

  isNotification(): boolean {
    return this.notifications?.length > 0;
  }

  clearAction(): void {
    this.configurationFacade.clearNotifications();
  }

  closeAction(notificationId: number): void {
    this.configurationFacade.closeNotification(notificationId);
  }

  handleActions(type: string): void {
    this.store.dispatch({type});
    this.clearAction();
  }

  getIcon(notification: IIndexedNotification): string {
    return IconUtils.getStatusIcon(notification.type);
  }

  private subscribeClick(): void {
    if (!this.notificationModals) return;
    this.clickSubscription = fromEvent(document, 'click')
      .pipe(
        filter((event: Event) => this.active && !this.notificationModals.some(ref => ref.nativeElement.contains(event.target)))
      ).subscribe(() => {
        this.active = false;
        this.unsubscribeClick();
      });
  }

  private unsubscribeClick(): void {
    this.clearAction();
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
      this.clickSubscription = undefined!;
    }
  }
}
